import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import newCalendarReducer, {
  initialState as defaultInitialState,
} from '../store/reducers/calendarReducer';

const CalendarStateContext = createContext(null);
const CalendarDispatchContext = createContext(null);
const CalendarRequestInteractionsContext = createContext({});

export const useCalendarState = () => {
  const ctx = useContext(CalendarStateContext);
  if (!ctx) {
    throw new Error('useCalendarState must be used within a CalendarProvider');
  }
  return ctx;
};

export const useCalendarDispatch = () => {
  const ctx = useContext(CalendarDispatchContext);
  if (!ctx) {
    throw new Error(
      'useCalendarDispatch must be used within a CalendarProvider'
    );
  }
  return ctx;
};

export const useCalendarRequestInteractions = () =>
  useContext(CalendarRequestInteractionsContext);

/**
 * Local provider (NO redux): state is stored via useReducer(newCalendarReducer).
 */
export const CalendarLocalProvider = ({
  children,
  initialState,
  requestInteractions = {},
}) => {
  const [state, dispatch] = useReducer(
    newCalendarReducer,
    initialState || defaultInitialState
  );

  return (
    <CalendarStateContext.Provider value={state}>
      <CalendarDispatchContext.Provider value={dispatch}>
        <CalendarRequestInteractionsContext.Provider
          value={requestInteractions}
        >
          {children}
        </CalendarRequestInteractionsContext.Provider>
      </CalendarDispatchContext.Provider>
    </CalendarStateContext.Provider>
  );
};

/**
 * Redux adapter provider: reads state from redux and passes redux dispatch.
 * Keeps old Calendar page working while calendar UI becomes reusable.
 */
export const CalendarReduxProvider = ({
  children,
  requestInteractions = {},
}) => {
  const state = useSelector((s) => s.newCalendarStore);
  const dispatch = useDispatch();

  // keep referential stability for consumers that depend on it
  const memoState = useMemo(() => state, [state]);
  const memoDispatch = useMemo(() => dispatch, [dispatch]);

  return (
    <CalendarStateContext.Provider value={memoState}>
      <CalendarDispatchContext.Provider value={memoDispatch}>
        <CalendarRequestInteractionsContext.Provider
          value={requestInteractions}
        >
          {children}
        </CalendarRequestInteractionsContext.Provider>
      </CalendarDispatchContext.Provider>
    </CalendarStateContext.Provider>
  );
};
